/**
 * AdminLayout — dashboard admin con sidebar oscuro violeta.
 *
 * Responsive:
 *  - `≥ lg`: sidebar lateral sticky a la izquierda (256px). Paleta `primary-900`.
 *  - `< lg`: topbar con botón "Menú" + nombre de la sección activa. La nav
 *    (10 entradas + "Volver al sitio") se abre como `<Drawer side="left">`.
 *
 * F6a implementa header con breadcrumbs + activity log + perfil admin (en
 * cada página). Aquí solo gestionamos la navegación. F5/F6 añaden guard de
 * rol fuera de este componente.
 */
import { useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import {
  BarChart3,
  BookOpen,
  Calendar,
  CalendarDays,
  FileText,
  Image as ImageIcon,
  LayoutDashboard,
  Library,
  Mail,
  Menu,
  MessageSquareQuote,
  Music2,
  Settings,
  Users,
} from 'lucide-react';
import { Routes } from '../../lib/routes';
import { cn } from '../../lib/cn';
import { SkipLink } from '../../components/layout/SkipLink';
import { ScrollToTop } from '../../components/layout/ScrollToTop';
import { ScrollToTopButton } from '../../components/layout/ScrollToTopButton';
import { AccountSidebar, type AccountSidebarItem } from '../../components/layout/AccountSidebar';
import { Drawer } from '../../components/ui/Drawer';

const navItems: AccountSidebarItem[] = [
  { to: Routes.admin, label: 'Inicio', icon: LayoutDashboard, end: true },
  { to: Routes.adminBookings, label: 'Agendamiento', icon: Calendar },
  { to: Routes.adminEmails, label: 'Correos', icon: Mail },
  { to: Routes.adminGallery, label: 'Galería', icon: ImageIcon },
  { to: Routes.adminEvents, label: 'Eventos', icon: CalendarDays },
  { to: Routes.adminAudios, label: 'Audios', icon: Music2 },
  { to: Routes.adminContent, label: 'Contenido', icon: FileText },
  { to: Routes.adminSettings, label: 'Configuración', icon: Settings },
  { to: Routes.adminCatalogs, label: 'Catálogos', icon: Library },
  { to: Routes.adminTestimonials, label: 'Testimonios', icon: MessageSquareQuote },
  { to: Routes.adminResources, label: 'Recursos', icon: BookOpen },
  { to: Routes.adminUsers, label: 'Usuarios', icon: Users },
  { to: Routes.adminReports, label: 'Reportes', icon: BarChart3 },
];

function resolveActiveLabel(pathname: string): string {
  const exact = navItems.find((item) => item.to === pathname);
  if (exact) return exact.label;
  const prefix = navItems
    .filter((item) => pathname.startsWith(item.to + '/') || pathname.startsWith(item.to))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return prefix?.label ?? 'Panel admin';
}

export function AdminLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { pathname } = useLocation();
  const activeLabel = useMemo(() => resolveActiveLabel(pathname), [pathname]);

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-cream-100">
      {/* Restore scroll al cambiar de subruta dentro de /admin/*. */}
      <ScrollToTop />
      <SkipLink />

      {/* Desktop sidebar: visible >= lg.
          QA 2026-06-04: el `<aside>` ahora lleva `bg-primary-900` propio.
          Antes sólo el `<div>` interno del `AccountSidebar` tenía el fondo
          violeta; cuando el contenido (12 items + header + footer) excedía
          los 100vh, los hijos overflowaban más allá del `<div>` con
          `h-full` y dejaban una franja inferior que mostraba el
          `bg-cream-100` del root del AdminLayout. Los textos cream-50 del
          footer ("Reportes" + "Volver al sitio" + "Cerrar sesión")
          quedaban ilegibles contra el cream. Aplicar el bg también acá
          garantiza que la columna sea 100 % oscura de borde a borde,
          incluso si el sidebar scrollea internamente. */}
      <aside
        aria-label="Navegación admin"
        className="hidden lg:block lg:w-64 lg:min-h-screen lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto bg-primary-900"
      >
        <AccountSidebar items={navItems} variant="admin" />
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile topbar: visible < lg.
            Padding lateral igual al gutter del `<main>` para alinearse con
            el contenido. `py-3` reduce la altura respecto del `p-4` original
            (más compacto en pantallas estrechas). Sticky para que el botón
            menú esté accesible al hacer scroll. */}
        <div className="lg:hidden sticky top-0 z-sticky flex items-center gap-3 px-4 sm:px-6 py-3 bg-primary-900 text-cream-100 border-b border-primary-800">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Abrir navegación admin"
            aria-expanded={drawerOpen}
            aria-controls="admin-drawer"
            className={cn(
              'inline-flex items-center justify-center shrink-0',
              'min-h-11 min-w-11 rounded text-cream-100',
              'hover:bg-primary-800 transition-colors duration-2 ease-out',
            )}
          >
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>
          {/* `min-w-0` permite que `truncate` recorte etiquetas largas en vez
              de empujar el ancho. `text-h4` es más proporcionado para la
              franja sticky compacta. */}
          <p className="min-w-0 flex-1 font-display text-h4 sm:text-h3 truncate">{activeLabel}</p>
        </div>

        {/* El main provee el gutter horizontal para TODAS las páginas admin:
            evita que cada page tenga que repetir `px-4 ...`. En mobile,
            `w-full max-w-full overflow-x-hidden` blindan contra cualquier
            hijo (tabla muy ancha, barra de tabs con `min-w-fit`, badge
            largo) que pretenda empujar el viewport. En `md+` reabrimos
            `overflow-x-visible` para no recortar tooltips/dropdowns que
            puedan escapar verticalmente. */}
        <main
          id="main"
          tabIndex={-1}
          // Bloqueo estricto del dimensionamiento del `<main>` en mobile:
          // `flex-1` para crecer, `w-full max-w-[100vw] min-w-0` para
          // garantizar que NUNCA exceda el viewport ni se estire por un
          // hijo flex (tabs row con `flex-nowrap`, tabla larga, badge
          // ancho). `overflow-x-hidden` en mobile clipea cualquier residual;
          // `md:overflow-x-visible` deja que tooltips/dropdowns escapen
          // verticalmente en desktop. Se mantiene el padding lateral
          // (gutter común de todas las páginas admin) aparte de estas
          // clases de dimensionamiento estricto.
          className="flex-1 w-full max-w-[100vw] min-w-0 overflow-x-hidden md:overflow-x-visible px-4 sm:px-6 lg:px-8 focus:outline-none"
        >
          <Outlet />
        </main>
      </div>

      {/* Drawer: solo visible en < lg. */}
      <div id="admin-drawer" className="lg:hidden">
        <Drawer.Root
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          side="left"
          size="sm"
          title="Panel admin"
          hideTitle
        >
          {/* CEC-261012-0721: el panel del Drawer tiene `overflow-hidden` y el
              alto de la pantalla, así que el menú necesita su propia zona con
              desplazamiento para llegar hasta "Cerrar sesión" en el celular
              (igual que `lg:overflow-y-auto` del menú de computador).
              `min-h-0` deja que la zona se encoja dentro del panel flex y
              `overscroll-contain` evita que se mueva la página de atrás. */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain bg-primary-900">
            <AccountSidebar
              items={navItems}
              variant="admin"
              onItemClick={() => setDrawerOpen(false)}
            />
          </div>
        </Drawer.Root>
      </div>

      {/* Flecha "volver al inicio" — disponible en /admin y subrutas. */}
      <ScrollToTopButton />
    </div>
  );
}
